import { useCallback, useEffect, useMemo, useState } from "react"
import { SidebarMenu } from "components"
import "styles/Pages/Onsix.scss"
import axios from "axios"
import { useAppSelector } from "hooks/reduxHooks"
import {
    CircularProgress,
    Box,
    Card,
    CardContent,
    Grid,
    Button,
    FormControlLabel,
    Checkbox,
    TextField,
    Stack,
    Typography,
    Divider,
    Paper,
    AppBar,
    Toolbar,
} from "@mui/material"
import ClientsTable from "components/on-six/ClientsTable"
import RatingByClient from "components/on-six/RatingByClient"
import AllsRatings from "components/on-six/AllsRatings"
import BrandReports from "components/on-six/BrandsReport"


type OptionFilters = "dateFilter" | "channels" | "stars" | "brands"

interface ITabsValues {
    id: 'clients' | 'history' | 'alls' | 'brands'
    title: string
    description: string
    children?: React.ReactNode
    filters: OptionFilters[]
}

const IndexOnSix = () => {
    const [loader, setLoader] = useState<boolean>(false);
    const [clients, setClients] = useState<any[]>([]);
    const [fullClients, setFullClients] = useState<any[]>([]);
    const { token, vendorCode } = useAppSelector(state => state.user);

    const [activeTab, setActiveTab] = useState<ITabsValues['id']>('clients');

    const [filterFrom, setFilterFrom] = useState<string | null>(null);
    const [filterTo, setFilterTo] = useState<string | null>(null);
    const [availableChannels, setAvailableChannels] = useState<string[]>([]);
    const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [availableBrands, setAvailableBrands] = useState([
        { "Code": "M01", "Name": "Marca Alpha" },
        { "Code": "M02", "Name": "Marca Beta" },
        { "Code": "M03", "Name": "Marca Gamma" },
        { "Code": "M04", "Name": "Marca Delta" },
        { "Code": "M05", "Name": "Marca Epsilon" },
        { "Code": "M06", "Name": "Marca Zeta" },
        { "Code": "M07", "Name": "Marca Theta" },
        { "Code": "M08", "Name": "Marca Omega" }
    ]);
    const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
    const [selectedStars, setSelectedStars] = useState<number[]>([]);

    const ValuesPath: ITabsValues[] = [
        {
            id: 'clients',
            title: 'Clientes',
            description: 'Gestiona la lista de los clientes',
            children: <ClientsTable clients={clients} />,
            filters: ["channels"]
        }, {
            id: 'history',
            title: 'Historico de Clientes',
            description: 'Revisa las calificaciones por cliente',
            children: <RatingByClient clients={clients} />,
            filters: ["channels"]
        }, {
            id: 'alls',
            title: 'Todas las calificaciones',
            description: 'Revisa las calificaciones de todos los clientes',
            children: <AllsRatings {...({ selectedStars } as any)} />,
            filters: ["dateFilter", "stars"]
        }, {
            id: 'brands',
            title: 'Marcas',
            description: 'Revisa las calificaciones de todas las marcas',
            children: <BrandReports />,
            filters: ["dateFilter", "brands"]
        }
    ]

    const getClients = useCallback(async (signal: AbortSignal) => {
        if (!token || fullClients.length > 0) return;
        setLoader(true);
        const bodyPayload: any = {
            validateAddress: true
        };

        const response = await axios.post(`${process.env.REACT_APP_SERVICE_URL}/sapclientlist/get/pricelist/seller`, {
            data: bodyPayload
        }, {
            headers: {
                'Authorization': `UserToken ${token}`,
                'Content-Type': 'application/json',
            }, signal
        }).then((res: any) => res).catch((err: any) => {
            console.error("Error fetching clients:", err);
            return {
                data: null,
                message: err.message
            };
        });
        if ('message' in response && response.message === 'canceled') return;
        if (response?.data?.data) {
            const dataArr = response.data?.data || [];
            setClients(dataArr);
            setFullClients(dataArr);

            try {
                const chans = new Set<string>();
                (dataArr || []).forEach((c: any) => {
                    const raw = c.U_canal ?? c.userId_canal ?? c.canal ?? c.channel ?? c.vendorCode;
                    if (!raw) return;
                    String(raw).split(',').map(s => s.trim()).filter(Boolean).forEach(p => chans.add(p));
                });
                if (vendorCode) {
                    String(vendorCode).split(',').map(s => s.trim()).filter(Boolean).forEach(p => chans.add(p));
                }
                const arr = Array.from(chans);
                if (arr.length && availableChannels.length === 0) {
                    setAvailableChannels(arr);
                    setSelectedChannels(arr.slice());
                }
            } catch (e) {
            }
        } else {
            setClients([]);
        }
        setLoader(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, vendorCode]);

    useEffect(() => {
        const controller = new AbortController();
        getClients(controller.signal);
        return () => controller.abort();
    }, [getClients]);

    const getBrandNameByCode = (code: string) => {
        const found = availableBrands.find(b => b.Code === code);
        return found ? found.Name : code;
    };

    useMemo(() => {
        const onlyChannelFilter = !filterFrom && !filterTo;
        // Si no hay filtros de fecha, aplicamos filtros locales (canal + marca)
        if (onlyChannelFilter) {
            // si no hay selección en canales ni marcas, dejar lista vacía
            if ((selectedChannels.length === 0) && (selectedBrands.length === 0)) {
                setClients([]);
                return;
            }

            const filtered = fullClients.filter((c: any) => {
                const raw = String(c.U_canal ?? c.userId_canal ?? c.canal ?? c.channel ?? c.vendorCode ?? '');
                const chans = raw.split(',').map((s: string) => s.trim()).filter(Boolean);

                const rawBrand = String(c.brand ?? c.U_brand ?? c.marca ?? c.brandName ?? c.marcaName ?? c.marca_cliente ?? '');
                const brands = rawBrand.split(',').map((s: string) => s.trim()).filter(Boolean);

                const channelOk = selectedChannels.length === 0 ? true : selectedChannels.some(ch => chans.includes(ch));
                const brandOk = selectedBrands.length === 0 ? true : selectedBrands.some(code => brands.includes(code) || brands.includes(getBrandNameByCode(code)));

                return channelOk && brandOk;
            });
            setClients(filtered);
            return;
        } else {
            console.log("Applying full filter with date range");
            // Aquí podrías combinar también canales/marcas con el filtro por fecha si lo deseas
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedChannels, selectedBrands, filterFrom, filterTo, fullClients]);


    const clearFilters = () => {
        setFilterFrom(null);
        setFilterTo(null);
        const resetChannels = availableChannels.length ? availableChannels.slice() : (vendorCode ? [vendorCode] : []);
        setSelectedChannels(resetChannels);

        const resetBrands = availableBrands.length ? availableBrands.map(b => b.Code) : [];
        setSelectedBrands(resetBrands);

        console.log("clearFilters - resetChannels", fullClients.length);
        if (fullClients.length > 0) {
            setClients(fullClients.slice());
        } else setClients(fullClients)
    };

    const currentTabConfig = ValuesPath.find(tab => tab.id === activeTab);

    return (
        <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "grey.50" }}>
            {/* Sidebar */}
            <Box sx={{ width: 240, flexShrink: 0, borderRight: 1, borderColor: "divider", bgcolor: "white" }}>
                <SidebarMenu pageName="on6" />
            </Box>

            {/* Main content */}
            <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
                {/* AppBar */}
                <AppBar position="sticky" color="default" elevation={2}>
                    <Toolbar>
                        <Typography variant="h6" fontWeight={600}>
                            On6
                        </Typography>
                    </Toolbar>
                </AppBar>

                {/* Tabs */}
                <Box sx={{ p: 2, bgcolor: "white", borderBottom: 1, borderColor: "divider" }}>
                    <Grid container spacing={2}>
                        {ValuesPath.map((tab) => (
                            <Grid size={3} key={tab.id}>
                                <Card
                                    onClick={() => setActiveTab(tab.id)}
                                    sx={{
                                        cursor: "pointer",
                                        transition: "all 0.3s ease",
                                        bgcolor: activeTab === tab.id ? "primary.main" : "white",
                                        color: activeTab === tab.id ? "white" : "inherit",
                                        border: activeTab === tab.id ? "2px solid" : "1px solid",
                                        borderColor: activeTab === tab.id ? "primary.main" : "divider",
                                        transform: activeTab === tab.id ? "scale(1.02)" : "scale(1)",
                                        "&:hover": {
                                            boxShadow: 3,
                                        },
                                    }}
                                >
                                    <CardContent sx={{ p: 2 }}>
                                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                            {tab.title}
                                        </Typography>
                                        <Typography variant="caption" color={activeTab === tab.id ? "inherit" : "text.secondary"}>
                                            {tab.description}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* Content area with fixed filters */}
                <Box sx={{ flexGrow: 1, display: "flex", overflow: "hidden" }}>
                    {/* Main content scrollable */}
                    <Box sx={{ flexGrow: 1, overflow: "auto", p: 2 }}>
                        {loader ? (
                            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            currentTabConfig?.children
                        )}
                    </Box>

                    {/* Filters sidebar fixed */}
                    <Paper
                        sx={{
                            width: 280,
                            flexShrink: 0,
                            borderLeft: 1,
                            borderColor: "divider",
                            p: 2,
                            overflowY: "auto",
                            maxHeight: "calc(100vh - 128px)", // AppBar + Tabs height
                        }}
                    >
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                            Filtros
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        {/* Date filters */}
                        {currentTabConfig?.filters.includes("dateFilter") && (
                            <Stack spacing={2} sx={{ mb: 3 }}>
                                <TextField
                                    label="Fecha desde"
                                    type="date"
                                    value={filterFrom ?? ''}
                                    onChange={(e) => setFilterFrom(e.target.value || null)}
                                    size="small"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                />
                                <TextField
                                    label="Fecha hasta"
                                    type="date"
                                    value={filterTo ?? ''}
                                    onChange={(e) => setFilterTo(e.target.value || null)}
                                    size="small"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                />
                                <Divider />
                            </Stack>
                        )}

                        {/* Channel filters */}
                        {currentTabConfig?.filters.includes("channels") && (
                            <Stack spacing={1.5} sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" fontWeight={600}>
                                    Canales
                                </Typography>
                                {availableChannels.length === 0 ? (
                                    <Typography variant="caption" color="text.secondary">
                                        Cargando canales...
                                    </Typography>
                                ) : (
                                    <>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={selectedChannels.length === availableChannels.length}
                                                    onChange={(e) => {
                                                        if (e.target.checked) setSelectedChannels(availableChannels.slice());
                                                        else setSelectedChannels([]);
                                                    }}
                                                />
                                            }
                                            label={<Typography variant="caption">Todos</Typography>}
                                        />
                                        <Box sx={{ display: "flex", flexDirection: "column", ml: 1 }}>
                                            {availableChannels.map(ch => (
                                                <FormControlLabel
                                                    key={ch}
                                                    control={
                                                        <Checkbox
                                                            size="small"
                                                            checked={selectedChannels.includes(ch)}
                                                            onChange={(e) => {
                                                                setSelectedChannels(prev => {
                                                                    if (e.target.checked) return Array.from(new Set([...prev, ch]));
                                                                    return prev.filter(x => x !== ch);
                                                                });
                                                            }}
                                                        />
                                                    }
                                                    label={<Typography variant="caption">{ch}</Typography>}
                                                />
                                            ))}
                                        </Box>
                                    </>
                                )}
                                <Divider />
                            </Stack>
                        )}

                        {/* Brand filters */}
                        {currentTabConfig?.filters.includes("brands") && (
                            <Stack spacing={1.5} sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" fontWeight={600}>
                                    Marcas
                                </Typography>
                                {availableBrands.length === 0 ? (
                                    <Typography variant="caption" color="text.secondary">
                                        Cargando marcas...
                                    </Typography>
                                ) : (
                                    <>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={selectedBrands.length === availableBrands.length}
                                                    onChange={(e) => {
                                                        if (e.target.checked) setSelectedBrands(availableBrands.map(b => b.Code));
                                                        else setSelectedBrands([]);
                                                    }}
                                                />
                                            }
                                            label={<Typography variant="caption">Todos</Typography>}
                                        />
                                        <Box sx={{ display: "flex", flexDirection: "column", ml: 1 }}>
                                            {availableBrands.map(b => (
                                                <FormControlLabel
                                                    key={b.Code}
                                                    control={
                                                        <Checkbox
                                                            size="small"
                                                            checked={selectedBrands.includes(b.Code)}
                                                            onChange={(e) => {
                                                                setSelectedBrands(prev => {
                                                                    if (e.target.checked) return Array.from(new Set([...prev, b.Code]));
                                                                    return prev.filter(x => x !== b.Code);
                                                                });
                                                            }}
                                                        />
                                                    }
                                                    label={<Typography variant="caption">{b.Name}</Typography>}
                                                />
                                            ))}
                                        </Box>
                                    </>
                                )}
                                <Divider />
                            </Stack>
                        )}

                        {/* Star filters */}
                        {currentTabConfig?.filters.includes("stars") && (
                            <Stack spacing={1.5} sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" fontWeight={600}>
                                    Estrellas
                                </Typography>
                                <Box>
                                    {[5, 4, 3, 2, 1].map((n) => (
                                        <FormControlLabel
                                            key={n}
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={selectedStars.includes(n)}
                                                    onChange={(e) => {
                                                        setSelectedStars(prev => {
                                                            if (e.target.checked) return Array.from(new Set([...prev, n])).sort((a, b) => b - a);
                                                            return prev.filter(x => x !== n);
                                                        });
                                                    }}
                                                />
                                            }
                                            label={<Typography variant="caption">{n} {'⭐'.repeat(n)}</Typography>}
                                        />
                                    ))}
                                </Box>
                                <Stack direction="row" spacing={1}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        fullWidth
                                        onClick={() => setSelectedStars([5, 4, 3, 2, 1])}
                                    >
                                        Todos
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        fullWidth
                                        onClick={() => setSelectedStars([])}
                                    >
                                        Limpiar
                                    </Button>
                                </Stack>
                                <Divider />
                            </Stack>
                        )}

                        {/* Action buttons */}
                        <Stack direction="row" spacing={1}>
                            <Button variant="contained" fullWidth>
                                Aplicar
                            </Button>
                            <Button variant="outlined" fullWidth onClick={clearFilters}>
                                Reset
                            </Button>
                        </Stack>

                        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
                            Los filtros se actualizan automáticamente al seleccionar opciones.
                        </Typography>
                    </Paper>
                </Box>
            </Box>
        </Box>
    )
}

export default IndexOnSix